import { WplywContext } from "../../../../../context/wplywContext";
import { useContext, useState, useEffect } from "react";
import { Input, DatePicker, Select } from "antd";
import dayjs from "dayjs";
import datetimeParser from "../../../../../components/datetimeParser";
import { useAuth } from "../../../../../context/AuthContext";
import Label from "../../../../../components/Label";
import ConfirmStatus from "../../MainWrapper/Confirm/ConfirmStatus";
import FixedNumberInput from "../../../../../components/FixedNumberInput";
import formatPrice from "../../../../../components/formatPrice";
import isCan from "../../../../../components/isCan";
import CommentWithFragments from "../../../../../components/CommentWithFragments";
import {
	buildPrzekazTitlePreview,
	TRANSFER_TITLE_MAX_LENGTH,
} from "../../../../../components/transferTitleUtils";
const { TextArea } = Input;

const StageData = () => {
	const { wplyw, setWplyw } = useContext(WplywContext);
	const [fieldsPerms, setFieldsPerms] = useState(null);
	const wplywData = wplyw?.wplyw_data?.data?.msr_wplywy || {};
	const selectedBankAccountOption = (
		wplyw?.wplyw_data?.data?.default_values?.msr_bank_accounts || []
	).find((opt) => opt?.value === wplywData?.msr_wplywy_bank_accounts_id);
	const selectedBankAccountLabel = String(
		selectedBankAccountOption?.label ||
			wplywData?.msr_bank_accounts_name ||
			"",
	);
	const isKancelariaAccount = selectedBankAccountLabel
		.toLowerCase()
		.includes("kancelaria");
	const isToForward = Number(wplywData?.msr_wplywy_to_forward || 0) === 1;
	const transferTitleText = String(
		wplywData?.msr_wplywy_przekaz_title_text ??
			wplywData?.msr_wplywy_bank_desc ??
			"",
	);
	const transferTitlePreview = buildPrzekazTitlePreview(transferTitleText);
	const isTransferTitleTooLong =
		isToForward && transferTitlePreview.length > TRANSFER_TITLE_MAX_LENGTH;
	const { user } = useAuth();
	const perms = user.perms;
	const isDisabled = wplyw.mode === "preview" || ConfirmStatus();
	const canEditToForward = Boolean(fieldsPerms?.msr_wplywy_to_forward?.edit);
	const labelstyle = {
		color: "black",
		fontWeight: "500",
		fontSize: "16px",
		marginBottom: "8px",
	};
	const valuestyle = {
		color: "black",
		fontWeight: "700",
		fontSize: "16px",
	};
	const checkboxLabelStyle = (disabled = false) => ({
		display: "flex",
		alignItems: "center",
		gap: "14px",
		width: "100%",
		padding: "13px 14px",
		border: "1px solid #ddd5c8",
		borderRadius: "10px",
		background: disabled ? "#f5f5f5" : "#faf8f2",
		cursor: disabled ? "not-allowed" : "pointer",
		userSelect: "none",
		boxSizing: "border-box",
	});
	const checkboxBoxStyle = (checked) => ({
		width: "24px",
		height: "24px",
		border: checked ? "2px solid #bd8136" : "2px solid #a89b8f",
		borderRadius: "4px",
		background: checked ? "#bd8136" : "#fff",
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		flexShrink: 0,
	});
	const checkboxTickStyle = {
		fontSize: "13px",
		fontWeight: 700,
		lineHeight: 1,
		marginTop: "0px",
		color: "#fff",
	};
	const checkboxTitleStyle = (checked, disabled = false) => ({
		fontWeight: checked ? 700 : 600,
		fontSize: "14px",
		color: disabled ? "#999" : checked ? "#6b3f0d" : "#3d3d3d",
		lineHeight: 1.25,
	});
	const checkboxSubStyle = {
		fontSize: "11px",
		color: "#777",
		lineHeight: 1.25,
	};

	// Function to handle changes
	const recordChange = (
		prevState,
		existingChanges,
		field,
		oldValue,
		newValue,
	) => {
		const baseChanges = Array.isArray(existingChanges)
			? [...existingChanges]
			: [];
		if (!prevState?.wplyw_data?.data?.msr_wplywy) {
			return baseChanges;
		}
		const payload = {
			field,
			label: getLabel(field),
			oldValue,
			etap: "Dane wpływu",
			newValue,
			datetime: Math.floor(new Date().getTime() / 1000),
			user: user.user.mlg_users_id,
			wplywid: prevState.wplyw_data.data.msr_wplywy.msr_wplywy_own_id,
			objectid: prevState.wplyw_data.data.msr_wplywy.msr_wplywy_own_id,
			personid: null,
		};
		const existingIndex = baseChanges.findIndex(
			(change) => change.field === field,
		);
		if (existingIndex > -1) {
			baseChanges[existingIndex] = payload;
		} else {
			baseChanges.push(payload);
		}
		return baseChanges;
	};

	const handleChange = (field, value, date = false) => {
		setWplyw((prevWplyw) => {
			const canEditForwardInStage = Boolean(
				fieldsPerms?.msr_wplywy_to_forward?.edit,
			);
			const oldValue = prevWplyw.wplyw_data.data.msr_wplywy[field];
			const newValue = date ? new Date(value).getTime() / 1000 : value;

			const updatedData = {
				...prevWplyw.wplyw_data.data.msr_wplywy,
				[field]: newValue,
			};
			if (field === "msr_wplywy_bank_desc") {
				const currentTransferTitleText = String(
					prevWplyw?.wplyw_data?.data?.msr_wplywy
						?.msr_wplywy_przekaz_title_text ?? "",
				);
				const oldBankDesc = String(oldValue ?? "");
				if (
					!currentTransferTitleText ||
					currentTransferTitleText === oldBankDesc
				) {
					updatedData.msr_wplywy_przekaz_title_text = String(
						newValue ?? "",
					);
				}
			}
			if (field === "msr_wplywy_to_forward" && Number(newValue) !== 1) {
				updatedData.msr_wplywy_to_forward_ready = 0;
			}
			if (field === "msr_wplywy_to_forward" && Number(newValue) === 1) {
				const currentTransferTitleText = String(
					updatedData.msr_wplywy_przekaz_title_text ?? "",
				);
				if (!currentTransferTitleText) {
					updatedData.msr_wplywy_przekaz_title_text = String(
						updatedData.msr_wplywy_bank_desc ?? "",
					);
				}
			}

			// Jeśli zmieniono rachunek wpływu na inny niż "Kancelaria", automatycznie wyłącz przekazanie.
			if (field === "msr_wplywy_bank_accounts_id") {
				const nextOpt = (
					prevWplyw?.wplyw_data?.data?.default_values
						?.msr_bank_accounts || []
				).find((opt) => opt?.value === newValue);
				const nextLabel = String(nextOpt?.label || "");
				const nextIsKancelaria = nextLabel
					.toLowerCase()
					.includes("kancelaria");
				if (nextIsKancelaria) {
					if (canEditForwardInStage) {
						updatedData.msr_wplywy_to_forward = 1;
					}
				} else {
					updatedData.msr_wplywy_to_forward = 0;
					updatedData.msr_wplywy_to_forward_ready = 0;
				}
			}

			if (
				field === "msr_wplywy_date_in" &&
				updatedData.msr_wplywy_date_forward &&
				newValue > updatedData.msr_wplywy_date_forward
			) {
				updatedData.msr_wplywy_date_forward = "";
			}

			const changes = recordChange(
				prevWplyw,
				prevWplyw.changes,
				field,
				oldValue,
				newValue,
			);

			return {
				...prevWplyw,
				wplyw_data: {
					...prevWplyw.wplyw_data,
					data: {
						...prevWplyw.wplyw_data.data,
						msr_wplywy: updatedData,
					},
				},
				changes,
			};
		});
	};

	useEffect(() => {
		const checkPermissions = async () => {
			setFieldsPerms({
				msr_wplywy_date_in: {
					see: await isCan(["can_see_data_wplywu"], perms),
					edit: await isCan(["can_edit_data_wplywu"], perms),
				},
				msr_wplywy_bank_accounts_id: {
					see: await isCan(["can_see_rachunek_wplywu"], perms),
					edit: await isCan(["can_edit_rachunek_wplywu"], perms),
				},
				msr_wplywy_bank_desc: {
					see: await isCan(["can_see_opis_systemu_bankowego"], perms),
					edit: await isCan(
						["can_edit_opis_systemu_bankowego"],
						perms,
					),
				},
				msr_wplywy_primary_amount: {
					see: await isCan(["can_see_kwota_wplywu"], perms),
					edit: await isCan(["can_edit_kwota_wplywu"], perms),
				},
				msr_wplywy_primary_deduction: {
					see: await isCan(
						["can_see_kwota_odliczenia_wstepnego"],
						perms,
					),
					edit: await isCan(
						["can_edit_kwota_odliczenia_wstepnego"],
						perms,
					),
				},
				msr_wplywy_primary_deduction_comment: {
					see: await isCan(
						["can_see_uwagi_do_kwoty_odliczenia"],
						perms,
					),
					edit: await isCan(
						["can_edit_uwagi_do_kwoty_odliczenia"],
						perms,
					),
				},
				msr_wplywy_date_forward: {
					see: await isCan(["can_see_data_przekazania"], perms),
					edit: await isCan(["can_edit_data_przekazania"], perms),
				},
				msr_wplywy_to_forward: {
					see: true,
					edit: await isCan(["can_edit_wplywy_to_forward"], perms),
				},
			});
		};
		checkPermissions();
	}, [perms]);

	// Function to get the label for a field
	function getLabel(field) {
		const labels = {
			msr_wplywy_date_in: "Data wpływu",
			msr_wplywy_bank_desc: "Opis systemu bankowego",
			msr_wplywy_przekaz_title_text:
				"Tytuł przelewu XML przekazania (tekst w klamrach)",
			msr_wplywy_przekaz_title_preview:
				"Tytuł przelewu XML przekazania (podgląd)",
			msr_wplywy_primary_amount: "Kwota wpływu - pierwotna",
			msr_wplywy_primary_deduction: "Kwota odliczania wstępnego",
			msr_wplywy_bank_accounts_id: "Rachunek wpływu",
			msr_wplywy_date_forward: "Data wpływu na rachunek rozliczeniowy",
			msr_wplywy_comment: "Uwagi do etapu",
			msr_wplywy_to_forward:
				"Wpływ do automatycznego przekazania - generowanie XML do banku [KANCELARIA -> CDO]",
			msr_wplywy_to_forward_ready:
				"GOTOWY do przekazania [KANCELARIA -> CDO]",
		};
		return labels[field] || field;
	}

	const renderToggleCheckbox = ({
		checked,
		onChange,
		disabled,
		title,
		subtitle,
	}) => (
		<label style={checkboxLabelStyle(disabled)}>
			<input
				type="checkbox"
				checked={checked}
				disabled={disabled}
				onChange={(e) => onChange(e.target.checked ? 1 : 0)}
				style={{ display: "none" }}
			/>
			<span style={checkboxBoxStyle(checked)}>
				{checked && <span style={checkboxTickStyle}>✓</span>}
			</span>
			<span
				style={{
					display: "flex",
					flexDirection: "column",
					gap: "2px",
					justifyContent: "center",
				}}
			>
				<span style={checkboxTitleStyle(checked, disabled)}>
					{title}
				</span>
				{subtitle && <span style={checkboxSubStyle}>{subtitle}</span>}
			</span>
		</label>
	);

	return (
		fieldsPerms && (
			<div
				className="formwithpreview"
				style={{
					display: "flex",
					flexDirection: "column",
					gap: "16px",
				}}
			>
				<div
					style={{
						border: "1px solid #bd8136",
						padding: "16px",
						display: "flex",
						flexDirection: "column",
						gap: "16px",
					}}
				>
					<div style={{ fontWeight: 700, fontSize: "14px" }}>
						Wpływ
					</div>
					{(fieldsPerms.msr_wplywy_date_in.see ||
						fieldsPerms.msr_wplywy_date_in.edit) && (
						<div style={{ width: "100%" }}>
							<Label required>Data wpływu</Label>
							<DatePicker
								disabled={
									isDisabled ||
									!fieldsPerms.msr_wplywy_date_in.edit
								}
								defaultValue={
									wplywData.msr_wplywy_date_in
										? dayjs(
												datetimeParser(
													wplywData.msr_wplywy_date_in,
													"YYYY-MM-DD",
												),
												"YYYY-MM-DD",
											)
										: null
								}
								style={{ width: "100%" }}
								placeholder="Wybierz datę wpływu"
								onChange={(date, dateString) =>
									handleChange(
										"msr_wplywy_date_in",
										dateString,
										true,
									)
								}
								disabledDate={(current) => {
									return (
										current &&
										current > dayjs().endOf("day")
									);
								}}
								allowClear={false}
							/>
						</div>
					)}

					{(fieldsPerms.msr_wplywy_bank_accounts_id.see ||
						fieldsPerms.msr_wplywy_bank_accounts_id.edit) && (
						<div style={{ width: "100%" }}>
							<Label required>Rachunek wpływu</Label>
							<Select
								disabled={
									isDisabled ||
									!fieldsPerms.msr_wplywy_bank_accounts_id
										.edit
								}
								status={
									wplywData.msr_wplywy_bank_accounts_id
										? "default"
										: "error"
								}
								defaultValue={
									wplywData.msr_wplywy_bank_accounts_id ||
									undefined
								}
								placeholder="Wybierz rachunek"
								style={{ width: "100%" }}
								options={
									wplyw.wplyw_data.data.default_values
										.msr_bank_accounts
								}
								onChange={(value) =>
									handleChange(
										"msr_wplywy_bank_accounts_id",
										value,
									)
								}
							/>
							{isKancelariaAccount && (
								<div style={{ marginTop: 12 }}>
									{renderToggleCheckbox({
										checked:
											Number(
												wplywData.msr_wplywy_to_forward,
											) === 1,
										onChange: (value) =>
											handleChange(
												"msr_wplywy_to_forward",
												value,
											),
										disabled:
											isDisabled || !canEditToForward,
										title: "Wpływ do automatycznego przekazania [KANCELARIA -> CDO]",
										subtitle:
											"Zaznacz, jeśli wpływ ma zostać przekazany na rachunek rozliczeniowy",
									})}
								</div>
							)}
						</div>
					)}

					{(fieldsPerms.msr_wplywy_bank_desc.see ||
						fieldsPerms.msr_wplywy_bank_desc.edit) && (
						<div
							style={{
								display: "flex",
								gap: "16px",
								alignItems: "stretch",
							}}
						>
							<div style={{ flex: 1 }}>
								<Label required>Opis systemu bankowego</Label>
								<TextArea
									style={{ height: "10em", resize: "none" }}
									disabled={
										isDisabled ||
										!fieldsPerms.msr_wplywy_bank_desc.edit
									}
									status={
										wplywData.msr_wplywy_bank_desc
											? "default"
											: "error"
									}
									value={wplywData.msr_wplywy_bank_desc || ""}
									placeholder="Opis systemu bankowego"
									onChange={(e) =>
										handleChange(
											"msr_wplywy_bank_desc",
											e.target.value,
										)
									}
								/>
							</div>

							{isToForward && (
								<div style={{ flex: 1 }}>
									<Label>
										Tytuł przelewu XML (podgląd / edycja)
									</Label>
									<TextArea
										style={{
											height: "10em",
											resize: "none",
										}}
										disabled={isDisabled}
										status={
											isTransferTitleTooLong
												? "error"
												: "default"
										}
										value={transferTitlePreview}
										placeholder="PRZEK:..."
										onChange={(e) => {
											handleChange(
												"msr_wplywy_przekaz_title_text",
												e.target.value,
											);
										}}
									/>
								</div>
							)}
						</div>
					)}

					{isToForward && (
						<div
							style={{
								marginTop: "-8px",
								fontSize: "12px",
								color: isTransferTitleTooLong
									? "#ff4d4f"
									: "#595959",
							}}
						>
							Długość tytułu XML: {transferTitlePreview.length}/
							{TRANSFER_TITLE_MAX_LENGTH}
							{isTransferTitleTooLong
								? " - przekroczono limit, skróć opis systemu bankowego."
								: ""}
						</div>
					)}

					{(fieldsPerms.msr_wplywy_primary_amount.see ||
						fieldsPerms.msr_wplywy_primary_amount.edit) && (
						<div style={{ flex: 1 }}>
							<Label required>
								Kwota wpływu na rachunek bankowy
							</Label>
							<FixedNumberInput
								mode={wplyw.mode}
								disabled={
									isDisabled ||
									!fieldsPerms.msr_wplywy_primary_amount.edit
								}
								value={wplywData.msr_wplywy_primary_amount}
								status={
									wplywData.msr_wplywy_primary_amount &&
									wplywData.msr_wplywy_primary_amount > 0
										? "default"
										: "error"
								}
								placeholder="Wpisz kwotę"
								addonAfter="zł"
								onChange={(newValue) =>
									handleChange(
										"msr_wplywy_primary_amount",
										parseFloat(newValue),
									)
								}
							/>
						</div>
					)}
				</div>

				<div
					style={{
						border: "1px solid #bd8136",
						padding: "16px",
						display: "flex",
						flexDirection: "column",
						gap: "16px",
					}}
				>
					<div style={{ fontWeight: 700, fontSize: "14px" }}>
						Odliczenia wstępne
					</div>
					{(fieldsPerms.msr_wplywy_primary_deduction.see ||
						fieldsPerms.msr_wplywy_primary_deduction.edit) && (
						<div style={{ flex: 1 }}>
							<Label required>
								Kwota odliczania wstępnego [KZP, Opłaty
								Skarbowe, inne KANCELARIA]
							</Label>
							<FixedNumberInput
								mode={wplyw.mode}
								disabled={
									isDisabled ||
									!fieldsPerms.msr_wplywy_primary_deduction
										.edit
								}
								value={wplywData.msr_wplywy_primary_deduction}
								status={
									wplywData.msr_wplywy_primary_deduction !==
									undefined
										? "default"
										: "error"
								}
								placeholder="Wpisz kwotę"
								maxvalue={wplywData.msr_wplywy_primary_amount}
								addonAfter="zł"
								onChange={(newValue) => {
									const primaryAmount = parseFloat(
										wplywData.msr_wplywy_primary_amount ||
											0,
									);
									let deductionAmount =
										parseFloat(newValue).toFixed(2);
									if (deductionAmount > primaryAmount) {
										deductionAmount = primaryAmount;
									}
									handleChange(
										"msr_wplywy_primary_deduction",
										parseFloat(deductionAmount),
									);
								}}
							/>
						</div>
					)}

					{(fieldsPerms.msr_wplywy_primary_deduction_comment.see ||
						fieldsPerms.msr_wplywy_primary_deduction_comment
							.edit) && (
						<div>
							<label>Uwagi do kwoty odliczenia wstępnego</label>
							<TextArea
								style={{ height: "6em", resize: "none" }}
								disabled={
									isDisabled ||
									!fieldsPerms
										.msr_wplywy_primary_deduction_comment
										.edit
								}
								defaultValue={
									wplywData.msr_wplywy_primary_deduction_comment ||
									""
								}
								placeholder="Uwagi do kwoty odliczenia wstępnego"
								onChange={(e) =>
									handleChange(
										"msr_wplywy_primary_deduction_comment",
										e.target.value,
									)
								}
							/>
						</div>
					)}
				</div>

				<div
					style={{
						padding: "20px",
						border: "1px solid black",
						background: "#bd813626",
					}}
				>
					<div style={{ ...labelstyle, fontSize: "18px" }}>
						Kwota do przekazania na rachunek rozliczeniowy
					</div>
					<div style={{ ...valuestyle, fontSize: "20px" }}>
						{formatPrice(
							(wplywData?.msr_wplywy_primary_amount || 0) -
								(wplywData?.msr_wplywy_primary_deduction || 0),
						)}{" "}
						zł
					</div>
				</div>

				{isKancelariaAccount && isToForward && (
					<div
						style={{
							border: "1px solid #bd8136",
							padding: "16px",
							display: "flex",
							alignItems: "center",
						}}
					>
						{renderToggleCheckbox({
							checked:
								Number(
									wplywData.msr_wplywy_to_forward_ready,
								) === 1,
							onChange: (value) =>
								handleChange(
									"msr_wplywy_to_forward_ready",
									value,
								),
							disabled: isDisabled,
							title: "Gotowe do przekazania na rachunek rozliczeniowy",
							subtitle:
								"Zatwierdź przekazanie środków na rachunek rozliczeniowy",
						})}
					</div>
				)}

				<div
					style={{
						border: "1px solid #bd8136",
						padding: "16px",
						display: "flex",
						flexDirection: "column",
						gap: "16px",
					}}
				>
					<div style={{ fontWeight: 700, fontSize: "14px" }}>
						Przekazanie
					</div>
					{wplywData.msr_wplywy_przekaz_xml_generated_datetime && (
						<div
							style={{
								marginBottom: "6px",
								fontSize: "13px",
								color: "#595959",
							}}
						>
							{`Data wygenerowania XML przekazania: ${datetimeParser(
								wplywData.msr_wplywy_przekaz_xml_generated_datetime,
								"YYYY-MM-DD HH:mm",
							)}`}
						</div>
					)}

					{(fieldsPerms.msr_wplywy_date_forward.see ||
						fieldsPerms.msr_wplywy_date_forward.edit) && (
						<div>
							<Label>
								{Number(
									wplywData.msr_wplywy_to_forward || 0,
								) === 1
									? "Data wpływu na rachunek rozliczeniowy generowana automatycznie przy generowaniu XML"
									: "Data wpływu na rachunek rozliczeniowy"}
							</Label>
							<DatePicker
								allowClear={true}
								status={
									datetimeParser(
										wplywData.msr_wplywy_date_forward,
										"YYYY-MM-DD",
									) >
										datetimeParser(
											wplywData.msr_wplywy_date_in,
											"YYYY-MM-DD",
										) &&
									wplywData.msr_wplywy_date_forward !==
										"null" &&
									wplywData.msr_wplywy_date_forward
										? "warning"
										: ""
								}
								disabled={
									isDisabled ||
									!fieldsPerms.msr_wplywy_date_forward.edit
								}
								value={
									wplywData.msr_wplywy_date_forward &&
									wplywData.msr_wplywy_date_forward !== "null"
										? dayjs(
												datetimeParser(
													wplywData.msr_wplywy_date_forward,
													"YYYY-MM-DD",
												),
												"YYYY-MM-DD",
											)
										: null
								}
								style={{ width: "100%" }}
								placeholder="Wybierz datę"
								onChange={(date, dateString) =>
									handleChange(
										"msr_wplywy_date_forward",
										dateString,
										true,
									)
								}
								disabledDate={(current) => {
									const referenceDate = dayjs(
										datetimeParser(
											wplywData.msr_wplywy_date_in,
											"YYYY-MM-DD",
										),
										"YYYY-MM-DD",
									).startOf("day");
									const maxFutureDate = dayjs()
										.add(7, "days")
										.endOf("day");
									return (
										current &&
										(current.isBefore(
											referenceDate,
											"day",
										) ||
											current.isAfter(
												maxFutureDate,
												"day",
											))
									);
								}}
							/>
							{wplywData.msr_wplywy_date_forward !== "null" &&
							wplywData.msr_wplywy_date_forward ? (
								datetimeParser(
									wplywData.msr_wplywy_date_forward,
									"YYYY-MM-DD",
								) >
								datetimeParser(
									new Date().getTime() / 1000,
									"YYYY-MM-DD",
								) ? (
									<div
										style={{
											background: "#f58a2a",
											padding: "5px 15px",
											color: "white",
											marginTop: "5px",
										}}
									>
										Data wpływu na rachunek rozliczeniowy
										jest ustawiona na datę późniejszą niż
										data dzisiejsza!
									</div>
								) : (
									""
								)
							) : (
								<></>
							)}
						</div>
					)}

					<div>
						<label>Uwagi do wpływu</label>
						<CommentWithFragments
							commentName={"msr_wplywy_comment"}
							disabled={isDisabled}
							value={wplywData.msr_wplywy_comment || ""}
							defaultValue={wplywData.msr_wplywy_comment || ""}
							placeholder="Uwagi do etapu"
							onChange={(e) =>
								handleChange(
									"msr_wplywy_comment",
									e.target.value,
								)
							}
							style={{ height: "6em", resize: "none" }}
						/>
					</div>
				</div>
			</div>
		)
	);
};

export default StageData;
